import { StrategicRecommendation, StrategicRecommendationFeature } from 'types'
import Styles from './Row.module.scss'
import { Button } from 'blaze-ui/Button'
import { Text } from 'blaze-ui/Text/Text'
import { useModals } from 'almanac-ui/Modal'
import { DetailsModal } from './DetailsModal/DetailsModal'
import { Edit1 } from 'blaze-ui/icons'
import groupBy from 'lodash/groupBy'
import { Heading } from 'blaze-ui/Heading/Heading'
import { CHANNEL_LABELS } from './constants'
import { FeatureRecommendations } from './FeatureRecommendations'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { LoadingEllipses } from 'components/Wizards/Wizard/Form/SectionHeader'
import { useBatch } from 'hooks/queries/batches/useBatch'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useQueryClient } from 'react-query'
import { ResponsiveDesign } from '../../components/ResponsiveDesign'

interface RowProps {
  channel_type: StrategicRecommendation['channel_type']
  brandKitId: number
  recommendations: StrategicRecommendation[]
}

export const BID_FIELD: Record<
  StrategicRecommendation['channel_type'],
  | 'brain_social_strategic_recommendations_bid'
  | 'brain_seo_strategic_recommendations_bid'
  | 'brain_email_strategic_recommendations_bid'
> = {
  social: 'brain_social_strategic_recommendations_bid',
  seo: 'brain_seo_strategic_recommendations_bid',
  email: 'brain_email_strategic_recommendations_bid',
}

export const Row = ({ brandKitId, channel_type, recommendations }: RowProps) => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const { openModal } = useModals()
  const brandKit = useBrandKit(brandKitId).data?.brand_kit
  const bidField = BID_FIELD[channel_type]
  const batchId = brandKit?.[bidField]
  const isGenerating = typeof batchId === 'string'

  useBatch(batchId ?? undefined, {
    pollUntilFinal: true,
    onComplete: async () => {
      await queryClient.invalidateQueries([
        'workspaces',
        workspaceId,
        'brandKits',
        brandKitId,
        'strategicRecommendations',
      ])
      await queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits', 'detail', brandKitId])
    },
  })

  const groupedByFeature = groupBy(recommendations, 'feature')
  const title = CHANNEL_LABELS[channel_type]

  return (
    <>
      <ResponsiveDesign.Mobile>
        <div className={Styles.channelRecommendations}>
          <Heading level={2} color="var(--dark-80)">
            {title}
          </Heading>

          <hr className={Styles.divider} />

          {Object.keys(groupedByFeature).length > 0 &&
            Object.entries(groupedByFeature).map(([feature, recommendations]) => (
              <FeatureRecommendations
                key={feature}
                feature={feature as StrategicRecommendationFeature}
                recommendations={recommendations}
                brandKitId={brandKitId}
                channelType={channel_type}
              />
            ))}
        </div>
      </ResponsiveDesign.Mobile>

      <ResponsiveDesign.Desktop>
        <tbody>
          <tr>
            <td colSpan={2} className={Styles.titleCell}>
              <div className={Styles.titleContent}>
                <Heading level={3} color="var(--dark-80)">
                  {title}
                </Heading>
                {!isGenerating && (
                  <Button
                    frontIcon={Edit1}
                    size="lg"
                    variant="ghost"
                    onPress={() => {
                      openModal(DetailsModal, { channel_type, brandKitId, recommendations })
                    }}
                    aria-label={`Revise ${title}`}
                  >
                    Revise
                  </Button>
                )}
              </div>
            </td>
          </tr>

          {Object.keys(groupedByFeature).length === 0 && (
            <tr>
              <td colSpan={3} className={Styles.noRecommendationsMessage}>
                {isGenerating && (
                  <Text variant="primary" color="var(--dark-60)">
                    We're analyzing your content and generating recommendations
                    <LoadingEllipses />
                  </Text>
                )}
                {!isGenerating && (
                  <Text variant="primary" color="var(--dark-60)">
                    We're analyzing your content and generating recommendations, check back soon.
                  </Text>
                )}
              </td>
            </tr>
          )}

          {Object.keys(groupedByFeature).length > 0 &&
            Object.entries(groupedByFeature).map(([feature, recommendations]) => (
              <FeatureRecommendations
                key={feature}
                feature={feature as StrategicRecommendationFeature}
                recommendations={recommendations}
                brandKitId={brandKitId}
                channelType={channel_type}
              />
            ))}
        </tbody>
      </ResponsiveDesign.Desktop>
    </>
  )
}
